import { useMutation } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type {
    TApiODIN,
    TGrantAccountPermissionsRequest,
    TRevokeAccountPermissionsRequest,
    TNewCoADefinition,
} from "@/apis/odin/apiODIN";
import type { TSliceNotifications } from "@/store/notifications/sliceNotifications";
import type { TAccountDTO } from "@/store/accounts/accountTypes";
import type { TAlteredCustomAccount } from "@/store/sliceCustomChartOfAccountsMappings/sliceCustomChartOfAccountsMappings";
import { coaKeys, adminKeys } from "@/queries/queryKeys";

export const createCoaMutationHooks = ({
    apiODIN,
    queryClient,
    addNotificationWithDefaultTimeout,
}: {
    apiODIN: TApiODIN;
    queryClient: QueryClient;
    addNotificationWithDefaultTimeout: TSliceNotifications["addNotificationWithDefaultTimeout"];
}) => {
    const invalidateCoa = () =>
        queryClient.invalidateQueries({ queryKey: coaKeys.all });
    const invalidateAdmin = () =>
        queryClient.invalidateQueries({ queryKey: adminKeys.all });

    const useCreateDraftCoaMutation = () =>
        useMutation({
            mutationKey: ["coa", "createDraft"],
            mutationFn: async (data: TNewCoADefinition) => {
                const response = await apiODIN.createDraftCoa(data);
                return response.data;
            },
            onSuccess: () => {
                void invalidateCoa();
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to create draft COA",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useUpdateDraftCoaMutation = () =>
        useMutation({
            mutationKey: ["coa", "updateDraft"],
            mutationFn: async ({
                coaId,
                data,
            }: {
                coaId: number;
                data: TNewCoADefinition;
            }) => {
                const response = await apiODIN.updateDraftCoA(coaId, data);
                return response.data;
            },
            onSuccess: (_data, { coaId }) => {
                void queryClient.invalidateQueries({
                    queryKey: coaKeys.definition(coaId),
                });
                void invalidateCoa();
                addNotificationWithDefaultTimeout({
                    title: "Draft COA updated",
                    kind: "toast",
                    severity: "success",
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to update draft COA",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useCompleteCoaMutation = () =>
        useMutation({
            mutationFn: async (coaId: number) => {
                const response = await apiODIN.completeCoA(coaId);
                return response.data;
            },
            onSuccess: (_data, coaId) => {
                void queryClient.invalidateQueries({
                    queryKey: coaKeys.definition(coaId),
                });
                void queryClient.invalidateQueries({
                    queryKey: coaKeys.accounts(coaId),
                });
                void invalidateCoa();
            },
        });

    const useDiscardCoaMutation = () =>
        useMutation({
            mutationFn: async (coaId: number) => {
                await apiODIN.deleteDraftCoA(coaId);
            },
            onSuccess: () => {
                void invalidateCoa();
            },
        });

    const useAddAccountPermissionsMutation = () =>
        useMutation({
            mutationFn: async (
                accountPermissions: TGrantAccountPermissionsRequest,
            ) => {
                await apiODIN.addAccountPermissions(accountPermissions);
            },
            onSuccess: () => {
                addNotificationWithDefaultTimeout({
                    title: "Account permissions added",
                    kind: "toast",
                    severity: "success",
                });
                void invalidateAdmin();
                void invalidateCoa();
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to add account permissions",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useDeleteAccountPermissionsMutation = () =>
        useMutation({
            mutationFn: async (
                accountPermissions: TRevokeAccountPermissionsRequest,
            ) => {
                await apiODIN.deleteAccountPermissions(accountPermissions);
            },
            onSuccess: () => {
                addNotificationWithDefaultTimeout({
                    title: "Account permissions deleted",
                    kind: "toast",
                    severity: "success",
                });
                void invalidateAdmin();
                void invalidateCoa();
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to delete account permissions",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useSaveAccountsMutation = () =>
        useMutation({
            mutationFn: async ({
                coaId,
                accounts,
            }: {
                coaId: number;
                accounts: TAccountDTO[];
            }) => {
                await apiODIN.insertCoaAccounts(coaId, accounts);
            },
            onSuccess: (_data, { coaId }) => {
                void queryClient.invalidateQueries({
                    queryKey: coaKeys.accounts(coaId),
                });
                addNotificationWithDefaultTimeout({
                    title: "Accounts saved successfully",
                    kind: "toast",
                    severity: "success",
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Error saving accounts",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useSaveCustomAccountMappingsMutation = () =>
        useMutation({
            mutationKey: ["coa", "saveCustomAccountMappings"],
            mutationFn: async ({
                coaId,
                alteredCustomAccounts,
            }: {
                coaId: number;
                alteredCustomAccounts: TAlteredCustomAccount[];
            }) => {
                await apiODIN.saveAlteredCustomAccounts({
                    coaId,
                    alteredCustomAccounts,
                });
            },
            onSuccess: (_data, { coaId }) => {
                void queryClient.invalidateQueries({
                    queryKey: coaKeys.mappingTable(coaId),
                });
                void queryClient.invalidateQueries({
                    queryKey: coaKeys.auditLogs(coaId),
                });
                addNotificationWithDefaultTimeout({
                    title: "Mappings saved successfully",
                    kind: "toast",
                    severity: "success",
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Error saving mappings",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useAddCoAFileMutation = () =>
        useMutation({
            mutationFn: async ({
                coaId,
                file,
            }: {
                coaId: number | null;
                file: Blob | File;
            }) => {
                const response = await apiODIN.addCoAFile(coaId, file);
                return response.data;
            },
            onSuccess: (_data, { coaId }) => {
                if (coaId !== null) {
                    void queryClient.invalidateQueries({
                        queryKey: coaKeys.accounts(coaId),
                    });
                    void queryClient.invalidateQueries({
                        queryKey: coaKeys.definition(coaId),
                    });
                }
                void invalidateCoa();
            },
        });

    return {
        useCreateDraftCoaMutation,
        useUpdateDraftCoaMutation,
        useCompleteCoaMutation,
        useDiscardCoaMutation,
        useAddAccountPermissionsMutation,
        useDeleteAccountPermissionsMutation,
        useSaveAccountsMutation,
        useSaveCustomAccountMappingsMutation,
        useAddCoAFileMutation,
    };
};

export type TCoaMutationHooks = ReturnType<typeof createCoaMutationHooks>;
